(() => {
  // src/data/production-connection-methods.js
  var verification = Object.freeze({
    status: "verified",
    geometryVerified: true,
    compatibilityVerified: true,
    documentationNote: "Approved general Production V1 connection rule for compatible nominal rectangular grids."
  });
  var physicalAssessment = Object.freeze({
    status: "not-assessed",
    scope: null,
    claim: null,
    note: "The geometric rule does not certify universal real-part compatibility or physical validity."
  });
  var legalAssessment = Object.freeze({
    status: "not-assessed",
    scope: null,
    claim: null,
    note: "Legality and stress-free construction are not assessed by this geometric rule."
  });
  var NATIVE_RECTANGULAR_GRID_RULE_ID = "native-rectangular-grid-v1";
  var CELL_CENTRE_GRID_RULE_ID = "cell-centre-grid-v1";
  var PRODUCTION_CONNECTION_METHODS = Object.freeze([
    Object.freeze({
      id: NATIVE_RECTANGULAR_GRID_RULE_ID,
      ruleId: NATIVE_RECTANGULAR_GRID_RULE_ID,
      connectionPointCategory: "native-stud-centre",
      displayName: "Native rectangular grid",
      explanation: "Native stud-centre positions on a compatible nominal rectangular grid.",
      ruleOnly: true,
      enabled: true,
      pointKind: "native",
      pointType: "native-stud-centre-point",
      hosts: Object.freeze(["surface", "part"]),
      intermediary: null,
      allowedOrientations: Object.freeze({ kind: "all", values: Object.freeze([]) }),
      generators: Object.freeze([
        Object.freeze({ id: "surface-native-grid", host: "surface", pattern: "grid", unitsPerStud: 2 }),
        Object.freeze({ id: "part-native-grid", host: "part", pattern: "grid", unitsPerStud: 2 })
      ]),
      compatibility: Object.freeze({
        connectsTo: Object.freeze(["native-stud-centre-point", "cell-centre-point"]),
        constraints: Object.freeze([])
      }),
      verification,
      physicalAssessment,
      legalAssessment
    }),
    Object.freeze({
      id: CELL_CENTRE_GRID_RULE_ID,
      ruleId: CELL_CENTRE_GRID_RULE_ID,
      connectionPointCategory: "cell-centre",
      displayName: "Cell-centre grid",
      explanation: "Cell-centre positions halfway across both axes between four surrounding native grid points.",
      ruleOnly: true,
      enabled: true,
      pointKind: "derived",
      pointType: "cell-centre-point",
      hosts: Object.freeze(["surface", "part"]),
      intermediary: null,
      allowedOrientations: Object.freeze({ kind: "all", values: Object.freeze([]) }),
      generators: Object.freeze([
        Object.freeze({ id: "surface-cell-centre-grid", host: "surface", pattern: "cell-center", unitsPerStud: 2 }),
        Object.freeze({ id: "part-cell-centre-grid", host: "part", pattern: "cell-center", unitsPerStud: 2 })
      ]),
      compatibility: Object.freeze({
        connectsTo: Object.freeze(["native-stud-centre-point", "cell-centre-point"]),
        constraints: Object.freeze([])
      }),
      verification,
      physicalAssessment,
      legalAssessment
    })
  ]);

  // src/core/coordinate-system.js
  var COORDINATE_SYSTEM = Object.freeze({
    schemaVersion: 1,
    unitsPerStud: 2,
    unitName: "half-stud",
    axis: Object.freeze({ x: "right", y: "down" }),
    origin: "top-left-native-point"
  });
  function coordinateKey(point) {
    assertIntegerPoint(point);
    return `${point.x}:${point.y}`;
  }
  function assertIntegerPoint(point) {
    if (!point || !Number.isInteger(point.x) || !Number.isInteger(point.y)) {
      throw new TypeError("Connection-point coordinates must be integers.");
    }
    if (point.unitsPerStud !== COORDINATE_SYSTEM.unitsPerStud) {
      throw new RangeError("Connection point uses an unsupported coordinate scale.");
    }
    return point;
  }
  function unitsToStuds(units) {
    if (!Number.isFinite(units)) throw new TypeError("Coordinate units must be finite.");
    return units / COORDINATE_SYSTEM.unitsPerStud;
  }
  function boardBounds(board) {
    return Object.freeze({
      minX: -1,
      minY: -1,
      maxX: board.widthStuds * COORDINATE_SYSTEM.unitsPerStud - 1,
      maxY: board.heightStuds * COORDINATE_SYSTEM.unitsPerStud - 1
    });
  }
  function footprintCorners(footprint) {
    const maxX = footprint.widthStuds * COORDINATE_SYSTEM.unitsPerStud - 1;
    const maxY = footprint.lengthStuds * COORDINATE_SYSTEM.unitsPerStud - 1;
    return Object.freeze([
      Object.freeze({ x: -1, y: -1 }),
      Object.freeze({ x: maxX, y: -1 }),
      Object.freeze({ x: maxX, y: maxY }),
      Object.freeze({ x: -1, y: maxY })
    ]);
  }

  // src/core/connection-methods.js
  var VALID_POINT_KINDS = /* @__PURE__ */ new Set(["native", "derived"]);
  var VALID_HOSTS = /* @__PURE__ */ new Set(["surface", "part"]);
  var VALID_PATTERNS = /* @__PURE__ */ new Set(["grid", "between-x", "between-y", "cell-center"]);
  var VALID_ORIENTATION_KINDS = /* @__PURE__ */ new Set(["all", "enumerated"]);
  var VALID_CONSTRAINT_KINDS = /* @__PURE__ */ new Set(["anchor-role", "coordinate-kind"]);
  var VALID_ANCHOR_ROLES = /* @__PURE__ */ new Set(["start", "end"]);
  var VALID_COORDINATE_KINDS = /* @__PURE__ */ new Set(["native-center", "edge-half-x", "edge-half-y", "cell-center"]);
  var VALID_ASSESSMENT_STATUSES = /* @__PURE__ */ new Set(["not-assessed", "approved", "restricted"]);
  var VALID_POINT_CATEGORIES = /* @__PURE__ */ new Set(["native-stud-centre", "cell-centre", "edge-half-stud"]);
  function validateAssessment(method, fieldName) {
    const assessment = method[fieldName];
    if (!assessment || !VALID_ASSESSMENT_STATUSES.has(assessment.status)) {
      throw new TypeError(`${method.id} requires ${fieldName} data.`);
    }
    if (assessment.status === "approved" && (!assessment.scope?.trim() || !assessment.claim?.trim())) {
      throw new TypeError(`${method.id} requires a scoped ${fieldName} claim before approval.`);
    }
  }
  function validateAllowedOrientations(method) {
    const orientations = method.allowedOrientations;
    if (!orientations || !VALID_ORIENTATION_KINDS.has(orientations.kind)) {
      throw new TypeError(`${method.id} requires allowed orientation data.`);
    }
    if (orientations.kind === "enumerated") {
      if (!Array.isArray(orientations.values) || orientations.values.length === 0 || orientations.values.some((value) => !Number.isFinite(value) || value < 0 || value >= 360)) {
        throw new RangeError(`${method.id} has invalid enumerated orientations.`);
      }
    }
  }
  function validateCompatibilityConstraints(method) {
    const constraints = method.compatibility.constraints;
    if (!Array.isArray(constraints)) throw new TypeError(`${method.id} requires compatibility constraints.`);
    for (const constraint of constraints) {
      if (!constraint || !VALID_CONSTRAINT_KINDS.has(constraint.kind) || !Array.isArray(constraint.values) || constraint.values.length === 0) {
        throw new RangeError(`${method.id} has an invalid compatibility constraint.`);
      }
      if (constraint.kind === "anchor-role" && constraint.values.some((value) => !VALID_ANCHOR_ROLES.has(value))) {
        throw new RangeError(`${method.id} has an invalid anchor-role constraint.`);
      }
      if (constraint.kind === "coordinate-kind" && !VALID_HOSTS.has(constraint.host)) {
        throw new RangeError(`${method.id} has an invalid coordinate-kind host.`);
      }
      if (constraint.kind === "coordinate-kind" && constraint.values.some((value) => !VALID_COORDINATE_KINDS.has(value))) {
        throw new RangeError(`${method.id} has an invalid coordinate kind constraint.`);
      }
    }
  }
  function validateConnectionMethod(method, { allowDevelopment = false } = {}) {
    if (!method || typeof method !== "object") throw new TypeError("Connection method must be an object.");
    if (!/^[a-z][a-z0-9-]+$/.test(method.id ?? "")) throw new TypeError("Connection method requires a stable lowercase ID.");
    if (!method.displayName || !method.explanation) throw new TypeError(`${method.id} requires display text.`);
    if (!/^[a-z][a-z0-9-]+$/.test(method.ruleId ?? "")) throw new TypeError(`${method.id} requires a stable rule ID.`);
    if (!VALID_POINT_CATEGORIES.has(method.connectionPointCategory)) throw new RangeError(`${method.id} has an unsupported connection-point category.`);
    if (!VALID_POINT_KINDS.has(method.pointKind)) throw new RangeError(`${method.id} has an unsupported point kind.`);
    if (!/^[a-z][a-z0-9-]+$/.test(method.pointType ?? "")) throw new TypeError(`${method.id} requires a stable point type.`);
    if (!Array.isArray(method.hosts) || method.hosts.length === 0 || method.hosts.some((host) => !VALID_HOSTS.has(host))) {
      throw new RangeError(`${method.id} has unsupported hosts.`);
    }
    if (new Set(method.hosts).size !== method.hosts.length) throw new Error(`${method.id} contains duplicate hosts.`);
    if (method.developmentOnly && !allowDevelopment) throw new Error(`${method.id} is development-only.`);
    if (!Array.isArray(method.generators)) throw new TypeError(`${method.id} requires a generators array.`);
    const generatorIds = /* @__PURE__ */ new Set();
    for (const generator of method.generators) {
      if (!generator.id || !VALID_HOSTS.has(generator.host) || !method.hosts.includes(generator.host) || !VALID_PATTERNS.has(generator.pattern)) {
        throw new RangeError(`${method.id} contains an invalid generator.`);
      }
      if (generator.unitsPerStud !== COORDINATE_SYSTEM.unitsPerStud) {
        throw new RangeError(`${method.id} uses an unsupported coordinate scale.`);
      }
      if (generatorIds.has(generator.id)) throw new Error(`${method.id} contains duplicate generator IDs.`);
      generatorIds.add(generator.id);
    }
    if (method.hosts.some((host) => !method.generators.some((generator) => generator.host === host))) {
      throw new RangeError(`${method.id} requires a generator for every declared host.`);
    }
    if (!method.compatibility || !Array.isArray(method.compatibility.connectsTo)) {
      throw new TypeError(`${method.id} requires compatibility data.`);
    }
    if (method.compatibility.connectsTo.length === 0 || method.compatibility.connectsTo.some((pointType) => !/^[a-z][a-z0-9-]+$/.test(pointType))) {
      throw new TypeError(`${method.id} requires valid compatible point types.`);
    }
    validateCompatibilityConstraints(method);
    validateAllowedOrientations(method);
    if (!method.verification || !["draft", "research-needed", "verified"].includes(method.verification.status)) {
      throw new TypeError(`${method.id} requires verification data.`);
    }
    if (!method.verification.documentationNote?.trim() || typeof method.verification.geometryVerified !== "boolean" || typeof method.verification.compatibilityVerified !== "boolean") {
      throw new TypeError(`${method.id} requires explicit verification evidence and flags.`);
    }
    if (method.pointKind === "derived" && method.ruleOnly !== true && (!method.intermediary?.reference?.trim() || !method.intermediary?.displayName?.trim())) {
      throw new TypeError(`${method.id} requires intermediary metadata or an explicit rule-only scope for derived points.`);
    }
    validateAssessment(method, "physicalAssessment");
    validateAssessment(method, "legalAssessment");
    return method;
  }
  function validateConnectionMethods(methods, options = {}) {
    if (!Array.isArray(methods)) throw new TypeError("Connection-method catalogue must be an array.");
    const ids = /* @__PURE__ */ new Set();
    for (const method of methods) {
      validateConnectionMethod(method, options);
      if (ids.has(method.id)) throw new Error(`Duplicate connection method ID: ${method.id}`);
      ids.add(method.id);
    }
    return methods;
  }
  function getEnabledVerifiedMethods(methods, { allowDevelopment = false } = {}) {
    validateConnectionMethods(methods, { allowDevelopment });
    return Object.freeze(methods.filter((method) => method.enabled === true && method.verification.status === "verified" && method.verification.geometryVerified === true && method.verification.compatibilityVerified === true && (allowDevelopment || method.developmentOnly !== true)));
  }
  function arePointTypesCompatible(firstPoint, secondPoint, methodMap) {
    const firstMethod = methodMap.get(firstPoint.methodId);
    const secondMethod = methodMap.get(secondPoint.methodId);
    return Boolean(
      firstMethod && secondMethod && firstMethod.compatibility.connectsTo.includes(secondPoint.pointType) && secondMethod.compatibility.connectsTo.includes(firstPoint.pointType)
    );
  }
  function normalizedOrientation(value) {
    return (value % 360 + 360) % 360;
  }
  function orientationMatches(actual, expected, epsilon = 1e-9) {
    const difference = Math.abs(normalizedOrientation(actual) - normalizedOrientation(expected));
    return Math.min(difference, 360 - difference) <= epsilon;
  }
  function isConnectionMethodApplicable({ method, point, counterpart, anchorRole, rotationDegrees }) {
    if (!method.hosts.includes(point.host) || !VALID_ANCHOR_ROLES.has(anchorRole)) return false;
    if (!method.compatibility.connectsTo.includes(counterpart.pointType)) return false;
    if (method.allowedOrientations.kind === "enumerated" && !method.allowedOrientations.values.some((value) => orientationMatches(rotationDegrees, value))) return false;
    return method.compatibility.constraints.every((constraint) => {
      if (constraint.kind === "anchor-role") return constraint.values.includes(anchorRole);
      if (constraint.kind === "coordinate-kind" && constraint.host === point.host) return constraint.values.includes(point.coordinateKind);
      return true;
    });
  }
  function hasApprovedScopedAssessment(assessment) {
    return Boolean(assessment?.status === "approved" && assessment.scope?.trim() && assessment.claim?.trim());
  }

  // src/core/containment.js
  var CONTAINMENT_EPSILON = 1e-9;
  function isFootprintWithinBoard(polygon, board, epsilon = CONTAINMENT_EPSILON) {
    const bounds = boardBounds(board);
    return polygon.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y) && point.x >= bounds.minX - epsilon && point.x <= bounds.maxX + epsilon && point.y >= bounds.minY - epsilon && point.y <= bounds.maxY + epsilon);
  }

  // src/core/geometry.js
  function calculateSquaredDistance(pointA, pointB) {
    assertIntegerPoint(pointA);
    assertIntegerPoint(pointB);
    const dx = pointB.x - pointA.x;
    const dy = pointB.y - pointA.y;
    return dx * dx + dy * dy;
  }
  function createAnchorTransform({ localStart, localEnd, surfaceStart, surfaceEnd }) {
    const localDx = localEnd.x - localStart.x;
    const localDy = localEnd.y - localStart.y;
    const surfaceDx = surfaceEnd.x - surfaceStart.x;
    const surfaceDy = surfaceEnd.y - surfaceStart.y;
    const localLengthSquared = localDx * localDx + localDy * localDy;
    const surfaceLengthSquared = surfaceDx * surfaceDx + surfaceDy * surfaceDy;
    if (localLengthSquared === 0 || localLengthSquared !== surfaceLengthSquared) {
      throw new RangeError("Anchor vectors must be non-zero and have equal squared distance.");
    }
    const cos = (localDx * surfaceDx + localDy * surfaceDy) / localLengthSquared;
    const sin = (localDx * surfaceDy - localDy * surfaceDx) / localLengthSquared;
    const translateX = surfaceStart.x - (cos * localStart.x - sin * localStart.y);
    const translateY = surfaceStart.y - (sin * localStart.x + cos * localStart.y);
    return Object.freeze({ cos, sin, translateX, translateY });
  }
  function transformPoint(point, transform) {
    return Object.freeze({
      x: transform.cos * point.x - transform.sin * point.y + transform.translateX,
      y: transform.sin * point.x + transform.cos * point.y + transform.translateY
    });
  }
  function transformFootprint(footprint, transform) {
    return Object.freeze(footprintCorners(footprint).map((point) => transformPoint(point, transform)));
  }
  function calculatePlacementGeometry({ surfaceStart, surfaceEnd }) {
    const xUnits = surfaceEnd.x - surfaceStart.x;
    const yUnits = surfaceEnd.y - surfaceStart.y;
    const squaredUnits = xUnits * xUnits + yUnits * yUnits;
    let angleDegrees = Math.atan2(yUnits, xUnits) * (180 / Math.PI);
    if (angleDegrees < 0) angleDegrees += 360;
    return Object.freeze({
      offsets: Object.freeze({
        xUnits,
        yUnits,
        horizontalStuds: unitsToStuds(xUnits),
        verticalStuds: unitsToStuds(yUnits)
      }),
      distance: Object.freeze({ squaredUnits, studs: Math.sqrt(squaredUnits) / surfaceStart.unitsPerStud }),
      angleDegrees
    });
  }

  // src/core/distance-index.js
  function buildDistanceIndex(startPoint, candidatePoints) {
    const index = /* @__PURE__ */ new Map();
    for (const point of candidatePoints) {
      if (point.id === startPoint.id) continue;
      const signature = calculateSquaredDistance(startPoint, point);
      if (signature === 0) continue;
      const bucket = index.get(signature) ?? [];
      bucket.push(point);
      index.set(signature, bucket);
    }
    for (const bucket of index.values()) bucket.sort((a, b) => a.id.localeCompare(b.id));
    return index;
  }
  function getSharedDistanceSignatures(firstIndex, secondIndex) {
    return Object.freeze([...firstIndex.keys()].filter((signature) => secondIndex.has(signature)).sort((a, b) => a - b));
  }

  // src/core/point-generation.js
  function dimensionsForHost(host, model) {
    return host === "surface" ? { width: model.widthStuds, height: model.heightStuds } : { width: model.widthStuds, height: model.lengthStuds };
  }
  function coordinatesForPattern(pattern, width, height) {
    const points = [];
    if (pattern === "grid") {
      for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) points.push({ x: x * 2, y: y * 2, coordinateKind: "native-center" });
    } else if (pattern === "between-x") {
      for (let y = 0; y < height; y += 1) for (let x = 0; x < width - 1; x += 1) points.push({ x: x * 2 + 1, y: y * 2, coordinateKind: "edge-half-x" });
    } else if (pattern === "between-y") {
      for (let y = 0; y < height - 1; y += 1) for (let x = 0; x < width; x += 1) points.push({ x: x * 2, y: y * 2 + 1, coordinateKind: "edge-half-y" });
    } else if (pattern === "cell-center") {
      for (let y = 0; y < height - 1; y += 1) {
        for (let x = 0; x < width - 1; x += 1) {
          points.push({ x: x * 2 + 1, y: y * 2 + 1, coordinateKind: "cell-center" });
        }
      }
    }
    return points;
  }
  function generateConnectionPoints({ host, model, methods }) {
    const dimensions = dimensionsForHost(host, model);
    const points = [];
    for (const method of methods) {
      for (const generator of method.generators.filter((candidate) => candidate.host === host)) {
        for (const coordinate of coordinatesForPattern(generator.pattern, dimensions.width, dimensions.height)) {
          points.push(Object.freeze({
            id: `${host}:${method.id}:${generator.id}:${coordinate.x}:${coordinate.y}`,
            host,
            x: coordinate.x,
            y: coordinate.y,
            unitsPerStud: COORDINATE_SYSTEM.unitsPerStud,
            pointType: method.pointType,
            originKind: method.pointKind,
            coordinateKind: coordinate.coordinateKind,
            methodId: method.id,
            ruleId: method.ruleId,
            connectionPointCategory: method.connectionPointCategory,
            intermediaryReference: method.intermediary?.reference ?? null,
            explanation: method.explanation
          }));
        }
      }
    }
    return Object.freeze(points.sort((a, b) => a.y - b.y || a.x - b.x || a.id.localeCompare(b.id)));
  }
  function generateSurfacePoints(board, methods) {
    return generateConnectionPoints({ host: "surface", model: board, methods });
  }
  function generatePartPoints(footprint, methods) {
    return generateConnectionPoints({ host: "part", model: footprint, methods });
  }

  // src/core/placements.js
  function deterministicId(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `placement-${(hash >>> 0).toString(16).padStart(8, "0")}`;
  }
  function resultKey(result) {
    return [
      result.start.surfacePointId,
      result.start.partPointId,
      result.end.surfacePointId,
      result.end.partPointId,
      ...result.requiredMethodIds
    ].join("|");
  }
  var AXIS_ALIGNED_ROTATION_EPSILON_DEGREES = 1e-9;
  function distanceFromAxisAlignedRotation(rotationDegrees) {
    if (!Number.isFinite(rotationDegrees)) return 0;
    const moduloQuarterTurn = (rotationDegrees % 90 + 90) % 90;
    return Math.min(moduloQuarterTurn, 90 - moduloQuarterTurn);
  }
  function classifyPlacement({ offsets, rotationDegrees }, epsilon = AXIS_ALIGNED_ROTATION_EPSILON_DEGREES) {
    const boardVectorIsDiagonal = offsets?.xUnits !== 0 && offsets?.yUnits !== 0;
    const footprintIsDiagonal = distanceFromAxisAlignedRotation(rotationDegrees) > epsilon;
    return boardVectorIsDiagonal && footprintIsDiagonal ? "diagonal" : "straight";
  }
  function isDiagonalPlacement(result) {
    return result?.offsets != null && classifyPlacement({
      offsets: result.offsets,
      rotationDegrees: result.transform?.rotationDegrees
    }) === "diagonal";
  }
  function filterDiagonalPlacements(results) {
    return Object.freeze(results.filter(isDiagonalPlacement));
  }
  function deduplicatePlacements(results) {
    const unique = /* @__PURE__ */ new Map();
    for (const result of results) if (!unique.has(resultKey(result))) unique.set(resultKey(result), result);
    return Object.freeze([...unique.values()]);
  }
  function sortPlacements(results) {
    return Object.freeze([...results].sort((a, b) => a.end.surface.y - b.end.surface.y || a.end.surface.x - b.end.surface.x || a.transform.rotationDegrees - b.transform.rotationDegrees || a.end.partPointId.localeCompare(b.end.partPointId) || a.requiredMethodIds.join("|").localeCompare(b.requiredMethodIds.join("|"))));
  }
  function groupPlacementsByDestination(results) {
    const groups = /* @__PURE__ */ new Map();
    for (const result of results) {
      const key = coordinateKey({ ...result.end.surface, unitsPerStud: result.coordinateSystem.unitsPerStud });
      const group = groups.get(key) ?? [];
      group.push(result);
      groups.set(key, group);
    }
    return groups;
  }
  function findPlacements({ board, footprint, surfaceStartId, partStartId, methods, allowDevelopment = false }) {
    const enabledMethods = getEnabledVerifiedMethods(methods, { allowDevelopment });
    const methodMap = new Map(enabledMethods.map((method) => [method.id, method]));
    const surfacePoints = generateSurfacePoints(board, enabledMethods);
    const partPoints = generatePartPoints(footprint, enabledMethods);
    const surfaceStart = surfacePoints.find((point) => point.id === surfaceStartId);
    const partStart = partPoints.find((point) => point.id === partStartId);
    if (!surfaceStart || !partStart) {
      return Object.freeze({ surfacePoints, partPoints, results: Object.freeze([]), diagnostics: Object.freeze({ comparedPairs: 0 }) });
    }
    if (!arePointTypesCompatible(surfaceStart, partStart, methodMap)) {
      return Object.freeze({ surfacePoints, partPoints, results: Object.freeze([]), diagnostics: Object.freeze({ comparedPairs: 0 }) });
    }
    const surfaceIndex = buildDistanceIndex(surfaceStart, surfacePoints);
    const partIndex = buildDistanceIndex(partStart, partPoints);
    const signatures = getSharedDistanceSignatures(surfaceIndex, partIndex);
    const results = [];
    let comparedPairs = 0;
    for (const signature of signatures) {
      for (const partEnd of partIndex.get(signature)) {
        for (const surfaceEnd of surfaceIndex.get(signature)) {
          comparedPairs += 1;
          if (!arePointTypesCompatible(surfaceEnd, partEnd, methodMap)) continue;
          const transform = createAnchorTransform({ localStart: partStart, localEnd: partEnd, surfaceStart, surfaceEnd });
          const rotationDegrees = (Math.atan2(transform.sin, transform.cos) * 180 / Math.PI + 360) % 360;
          const applications = [
            { point: surfaceStart, counterpart: partStart, anchorRole: "start" },
            { point: partStart, counterpart: surfaceStart, anchorRole: "start" },
            { point: surfaceEnd, counterpart: partEnd, anchorRole: "end" },
            { point: partEnd, counterpart: surfaceEnd, anchorRole: "end" }
          ];
          if (!applications.every((application) => isConnectionMethodApplicable({
            ...application,
            method: methodMap.get(application.point.methodId),
            rotationDegrees
          }))) continue;
          const footprintPolygon = transformFootprint(footprint, transform);
          const containedWithinBoard = isFootprintWithinBoard(footprintPolygon, board);
          const geometry = calculatePlacementGeometry({ surfaceStart, surfaceEnd });
          const placementKind = classifyPlacement({ offsets: geometry.offsets, rotationDegrees });
          const requiredMethodIds = Object.freeze([.../* @__PURE__ */ new Set([
            surfaceStart.methodId,
            partStart.methodId,
            surfaceEnd.methodId,
            partEnd.methodId
          ])].sort());
          const key = [surfaceStart.id, partStart.id, surfaceEnd.id, partEnd.id, ...requiredMethodIds].join("|");
          const requiredMethods = requiredMethodIds.map((id) => methodMap.get(id));
          const requiredRules = Object.freeze(requiredMethods.map((method) => Object.freeze({
            ruleId: method.ruleId,
            methodId: method.id,
            connectionPointCategory: method.connectionPointCategory,
            pointKind: method.pointKind
          })));
          const requiredRuleIds = Object.freeze([...new Set(requiredRules.map((rule) => rule.ruleId))].sort());
          const physicallyVerified = requiredMethods.every((method) => hasApprovedScopedAssessment(method.physicalAssessment));
          const legallyApproved = requiredMethods.every((method) => hasApprovedScopedAssessment(method.legalAssessment));
          const assessmentRecords = (fieldName, approved) => Object.freeze(approved ? requiredMethods.map((method) => Object.freeze({
            methodId: method.id,
            scope: method[fieldName].scope,
            claim: method[fieldName].claim
          })) : []);
          results.push(Object.freeze({
            id: deterministicId(key),
            coordinateSystem: board.coordinateSystem,
            start: Object.freeze({
              surfacePointId: surfaceStart.id,
              partPointId: partStart.id,
              surface: surfaceStart,
              part: partStart
            }),
            end: Object.freeze({
              surfacePointId: surfaceEnd.id,
              partPointId: partEnd.id,
              surface: surfaceEnd,
              part: partEnd
            }),
            transform: Object.freeze({ ...transform, rotationDegrees }),
            transformedPartPoints: Object.freeze([partStart, partEnd].map((point) => transformPoint(point, transform))),
            footprintPolygon,
            offsets: geometry.offsets,
            distance: geometry.distance,
            angleDegrees: geometry.angleDegrees,
            placementKind,
            requiredMethodIds,
            requiredRuleIds,
            requiredRules,
            support: Object.freeze({
              geometricallyMatching: true,
              methodSupported: true,
              physicallyVerified,
              physicalStatus: physicallyVerified ? "approved" : "not-assessed",
              physicalAssessments: assessmentRecords("physicalAssessment", physicallyVerified),
              legalStatus: legallyApproved ? "approved" : "not-assessed",
              legalAssessments: assessmentRecords("legalAssessment", legallyApproved)
            }),
            containedWithinBoard,
            overhangStatus: containedWithinBoard ? "contained" : "partial"
          }));
        }
      }
    }
    return Object.freeze({
      surfacePoints,
      partPoints,
      results: sortPlacements(deduplicatePlacements(results)),
      diagnostics: Object.freeze({
        surfacePointCount: surfacePoints.length,
        partPointCount: partPoints.length,
        sharedSignatureCount: signatures.length,
        comparedPairs,
        cartesianPairCount: surfacePoints.length * partPoints.length
      })
    });
  }

  // src/core/validation.js
  var LIMITS = Object.freeze({
    board: Object.freeze({ min: 1, max: 64 }),
    footprint: Object.freeze({ min: 1, max: 32 })
  });
  function parseWholeStudDimension(rawValue, { label, min, max }) {
    const raw = String(rawValue ?? "").trim();
    if (raw === "") return Object.freeze({ ok: false, raw, error: "required", message: `${label} is required.` });
    if (!/^\d+$/.test(raw)) {
      return Object.freeze({ ok: false, raw, error: "whole-number", message: `${label} must be a whole number.` });
    }
    const value = Number(raw);
    if (!Number.isSafeInteger(value) || value < min || value > max) {
      return Object.freeze({ ok: false, raw, error: "range", message: `${label} must be between ${min} and ${max} studs.` });
    }
    return Object.freeze({ ok: true, raw, value, error: null, message: "" });
  }
  function validateConfiguration(input, limits = LIMITS) {
    const parsedFields = {
      boardWidth: parseWholeStudDimension(input.boardWidth, { label: "Board width", ...limits.board }),
      boardHeight: parseWholeStudDimension(input.boardHeight, { label: "Board height", ...limits.board }),
      footprintWidth: parseWholeStudDimension(input.footprintWidth, { label: "Footprint width", ...limits.footprint }),
      footprintLength: parseWholeStudDimension(input.footprintLength, { label: "Footprint length", ...limits.footprint })
    };
    const fields = Object.freeze(parsedFields);
    const ok = Object.values(fields).every((field) => field.ok);
    if (!ok) return Object.freeze({ ok: false, fields });
    return Object.freeze({
      ok: true,
      fields,
      board: Object.freeze({
        widthStuds: fields.boardWidth.value,
        heightStuds: fields.boardHeight.value,
        coordinateSystem: COORDINATE_SYSTEM
      }),
      footprint: Object.freeze({
        widthStuds: fields.footprintWidth.value,
        lengthStuds: fields.footprintLength.value,
        coordinateSystem: COORDINATE_SYSTEM
      })
    });
  }

  // src/presentation/formatting.js
  function formatNumber(value, maximumFractionDigits = 3) {
    if (!Number.isFinite(value)) throw new TypeError("Cannot format a non-finite number.");
    return new Intl.NumberFormat("en", {
      maximumFractionDigits,
      useGrouping: false
    }).format(Object.is(value, -0) ? 0 : value);
  }
  function formatSigned(value) {
    const normalized = Object.is(value, -0) ? 0 : value;
    return `${normalized > 0 ? "+" : ""}${formatNumber(normalized)}`;
  }
  function formatPoint(point) {
    return `X ${formatNumber(point.x / point.unitsPerStud + 1)}, Y ${formatNumber(point.y / point.unitsPerStud + 1)}`;
  }

  // src/presentation/result-text.js
  function createResultText(result, methods) {
    const methodMap = new Map(methods.map((method) => [method.id, method]));
    const methodNames = result.requiredMethodIds.map((id) => methodMap.get(id)?.displayName ?? id);
    const intermediaryNames = result.requiredMethodIds.map((id) => methodMap.get(id)?.intermediary?.displayName).filter(Boolean);
    const legality = result.support.legalStatus === "approved" ? `Legal status: ${result.support.legalAssessments.map((assessment) => `${assessment.claim} Scope: ${assessment.scope}`).join(" ")}` : "Legal and stress-free status: not assessed.";
    const physical = result.support.physicallyVerified ? `Physical assessment: ${result.support.physicalAssessments.map((assessment) => `${assessment.claim} Scope: ${assessment.scope}`).join(" ")}` : "Physical validity: not assessed.";
    const containment = result.containedWithinBoard ? "Containment: the complete nominal footprint is inside the board." : "Containment: the nominal footprint partially extends outside the board.";
    return [
      `Destination ${formatPoint(result.end.surface)}.`,
      `Angle ${formatNumber(result.angleDegrees)} degrees clockwise from right.`,
      `Horizontal offset ${formatSigned(result.offsets.horizontalStuds)} studs.`,
      `Vertical offset ${formatSigned(result.offsets.verticalStuds)} studs, positive downward.`,
      `Diagonal distance ${formatNumber(result.distance.studs)} studs.`,
      `Connection rules: ${methodNames.join(", ")}.`,
      intermediaryNames.length ? `Intermediary: ${intermediaryNames.join(", ")}.` : "",
      containment,
      physical,
      legality
    ].filter(Boolean).join(" ");
  }

  // src/presentation/view-model.js
  function groupPoints(points) {
    const groups = /* @__PURE__ */ new Map();
    for (const point of points) {
      const key = coordinateKey(point);
      const group = groups.get(key) ?? [];
      group.push(point);
      groups.set(key, group);
    }
    return [...groups.entries()].map(([key, variants]) => Object.freeze({
      key,
      x: variants[0].x,
      y: variants[0].y,
      variants: Object.freeze(variants),
      hasNative: variants.some((point) => point.originKind === "native"),
      hasDerived: variants.some((point) => point.originKind === "derived"),
      hasCellCenter: variants.some((point) => point.coordinateKind === "cell-center"),
      hasEdgeHalf: variants.some((point) => point.coordinateKind === "edge-half-x" || point.coordinateKind === "edge-half-y")
    }));
  }
  function movementPhrase(value, positiveDirection, negativeDirection) {
    if (value === 0) return `0 studs ${positiveDirection}`;
    return `${formatStudMeasurement(Math.abs(value))} ${value > 0 ? positiveDirection : negativeDirection}`;
  }
  function formatStudMeasurement(value, { signed = false } = {}) {
    const number = signed ? formatSigned(value) : formatNumber(value);
    return `${number} ${Math.abs(value) === 1 ? "stud" : "studs"}`;
  }
  function createDestinationSummary(result) {
    if (!result) return null;
    const unitsPerStud = result.coordinateSystem.unitsPerStud;
    const xStuds = result.end.surface.x / unitsPerStud + 1;
    const yStuds = result.end.surface.y / unitsPerStud + 1;
    const deltaXStuds = result.offsets.horizontalStuds;
    const deltaYStuds = result.offsets.verticalStuds;
    return Object.freeze({
      xStuds,
      yStuds,
      deltaXStuds,
      deltaYStuds,
      destinationText: `Destination: X = ${formatStudMeasurement(xStuds)}, Y = ${formatStudMeasurement(yStuds)}`,
      offsetText: `Offset from start: \u0394X = ${formatStudMeasurement(deltaXStuds, { signed: true })}, \u0394Y = ${formatStudMeasurement(deltaYStuds, { signed: true })}`,
      movementText: `Move ${movementPhrase(deltaXStuds, "right", "left")} and ${movementPhrase(deltaYStuds, "down", "up")} from the start.`
    });
  }
  function createExplorerViewModel({ state, engine, methods }) {
    const diagonalResults = filterDiagonalPlacements(engine.results);
    const destinationGroups = groupPlacementsByDestination(diagonalResults);
    const selectedResult = diagonalResults.find((result) => result.id === state.selectedResultId) ?? null;
    const previewResult = diagonalResults.find((result) => result.id === state.previewResultId) ?? null;
    const activeResult = previewResult ?? selectedResult;
    const placedFootprintPoints = activeResult ? engine.partPoints.map((point) => Object.freeze({
      ...point,
      placed: transformPoint(point, activeResult.transform),
      anchorRole: point.id === activeResult.start.partPointId ? "start" : point.id === activeResult.end.partPointId ? "end" : null
    })) : [];
    return Object.freeze({
      surfaceGroups: Object.freeze(groupPoints(engine.surfacePoints)),
      partGroups: Object.freeze(groupPoints(engine.partPoints)),
      destinations: Object.freeze([...destinationGroups.entries()].map(([key, results]) => Object.freeze({
        key,
        x: results[0].end.surface.x,
        y: results[0].end.surface.y,
        resultIds: Object.freeze(results.map((result) => result.id)),
        resultVariants: Object.freeze(results.map((result) => Object.freeze({
          resultId: result.id,
          surfacePointId: result.end.surfacePointId
        }))),
        preferredResultId: results[0].id,
        variantCount: results.length
      }))),
      results: Object.freeze(diagonalResults.map((result) => Object.freeze({
        result,
        text: createResultText(result, methods)
      }))),
      straightResultCount: engine.results.length - diagonalResults.length,
      selectedResult,
      previewResult,
      activeResult,
      placedFootprintPoints: Object.freeze(placedFootprintPoints),
      destinationSummary: createDestinationSummary(activeResult)
    });
  }

  // src/ui/board-renderer.js
  function polygonPoints(points) {
    return points.map((point) => `${point.x},${point.y}`).join(" ");
  }
  function angleMarkerMetrics(board) {
    const minimumBoardSpan = Math.min(board.widthStuds * 2, board.heightStuds * 2);
    return Object.freeze({
      radius: Math.min(2.4, Math.max(0.7, minimumBoardSpan * 0.12)),
      fontSize: Math.min(1.2, Math.max(0.35, minimumBoardSpan * 0.025))
    });
  }
  function angleArc(result, radius) {
    const start = result.start.surface;
    const radians = result.angleDegrees * Math.PI / 180;
    const endX = start.x + radius * Math.cos(radians);
    const endY = start.y + radius * Math.sin(radians);
    const largeArc = result.angleDegrees > 180 ? 1 : 0;
    return `M ${start.x + radius} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY}`;
  }
  function pointMarker(point, classes, destination) {
    if (point.originKind === "native") {
      return `<circle class="htbi-diagonal-point htbi-diagonal-point-native ${classes}" cx="${point.x}" cy="${point.y}" r="${destination ? 0.42 : 0.31}"></circle>`;
    }
    const coordinateClass = point.coordinateKind === "cell-center" ? "htbi-diagonal-point-cell-center" : "htbi-diagonal-point-edge-half";
    return `<rect class="htbi-diagonal-point htbi-diagonal-point-derived ${coordinateClass} ${classes}" x="${point.x - 0.11}" y="${point.y - 0.11}" width="0.22" height="0.22" transform="rotate(45 ${point.x} ${point.y})"></rect>`;
  }
  function surfaceHitTargetPath(point) {
    return `M ${point.x} ${point.y - 1} L ${point.x + 1} ${point.y} L ${point.x} ${point.y + 1} L ${point.x - 1} ${point.y} Z`;
  }
  function orderedSurfaceVariants(variants) {
    return [...variants].sort((a, b) => Number(a.originKind === "derived") - Number(b.originKind === "derived") || a.id.localeCompare(b.id));
  }
  function resolveStartAnchorPoint(surfaceGroups, startId) {
    if (!startId) return null;
    for (const group of surfaceGroups) {
      const point = group.variants.find((variant) => variant.id === startId);
      if (point) return point;
    }
    return null;
  }
  function footprintMarkup(active, instanceKey) {
    if (!active) return "";
    const points = polygonPoints(active.footprintPolygon);
    if (active.containedWithinBoard) {
      return `<polygon class="htbi-diagonal-footprint-placement htbi-diagonal-footprint-contained" points="${points}"></polygon>`;
    }
    return `
    <polygon class="htbi-diagonal-footprint-placement htbi-diagonal-footprint-overhang" points="${points}" fill="url(#${instanceKey}-overhang-pattern)"></polygon>
    <polygon class="htbi-diagonal-footprint-placement htbi-diagonal-footprint-inside" points="${points}" clip-path="url(#${instanceKey}-board-clip)"></polygon>
    <polygon class="htbi-diagonal-footprint-outline" points="${points}"></polygon>`;
  }
  function placedFootprintPointMarkup(point) {
    const anchorClass = point.anchorRole ? ` htbi-diagonal-placed-footprint-anchor htbi-diagonal-placed-footprint-anchor-${point.anchorRole}` : "";
    const symbol = point.originKind === "native" ? `<circle class="htbi-diagonal-placed-footprint-point htbi-diagonal-placed-footprint-point-native${anchorClass}" data-placed-part-point-id="${point.id}" cx="${point.placed.x}" cy="${point.placed.y}" r="0.26"></circle>` : `<rect class="htbi-diagonal-placed-footprint-point htbi-diagonal-placed-footprint-point-derived${anchorClass}" data-placed-part-point-id="${point.id}" x="${point.placed.x - 0.08}" y="${point.placed.y - 0.08}" width="0.16" height="0.16" transform="rotate(45 ${point.placed.x} ${point.placed.y})"></rect>`;
    if (!point.anchorRole) return symbol;
    const label = point.anchorRole === "start" ? "1" : "2";
    return `<g class="htbi-diagonal-placed-footprint-anchor-group" data-placed-part-point-id="${point.id}" data-anchor-role="${point.anchorRole}">
    <circle class="htbi-diagonal-placed-footprint-anchor-halo" cx="${point.placed.x}" cy="${point.placed.y}" r="0.47"></circle>
    ${symbol}
    <text class="htbi-diagonal-placed-footprint-anchor-label" x="${point.placed.x + 0.48}" y="${point.placed.y - 0.4}">${label}</text>
  </g>`;
  }
  function boardSvgMarkup({ instanceKey, board, viewBox, viewModel, state }) {
    const active = viewModel.activeResult;
    const selected = viewModel.selectedResult;
    const destinationMap = new Map(viewModel.destinations.map((destination) => [destination.key, destination]));
    const startId = state.surfaceStartId;
    const startPoint = resolveStartAnchorPoint(viewModel.surfaceGroups, startId);
    const angleMetrics = angleMarkerMetrics(board);
    return `
    <svg class="htbi-diagonal-board-svg" data-board-svg tabindex="0" role="application"
      aria-label="Stud board. Use arrow keys to move the keyboard cursor and Enter to choose a native point. Supported destinations are also available as buttons below the board."
      viewBox="${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}">
      <defs>
        <pattern id="${instanceKey}-stud-pattern" x="0" y="0" width="2" height="2" patternUnits="userSpaceOnUse">
          <circle cx="0" cy="0" r="0.22" class="htbi-diagonal-stud-dot"></circle>
        </pattern>
        <pattern id="${instanceKey}-overhang-pattern" x="0" y="0" width="1.2" height="1.2" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="1.2" height="1.2" class="htbi-diagonal-overhang-pattern-base"></rect>
          <path d="M 0 0 V 1.2" class="htbi-diagonal-overhang-pattern-line"></path>
        </pattern>
        <clipPath id="${instanceKey}-board-clip">
          <rect x="-1" y="-1" width="${board.widthStuds * 2}" height="${board.heightStuds * 2}"></rect>
        </clipPath>
      </defs>
      <rect class="htbi-diagonal-board-surface" x="-1" y="-1" width="${board.widthStuds * 2}" height="${board.heightStuds * 2}"></rect>
      <rect class="htbi-diagonal-stud-pattern" x="-0.25" y="-0.25" width="${(board.widthStuds - 1) * 2 + 0.5}" height="${(board.heightStuds - 1) * 2 + 0.5}" fill="url(#${instanceKey}-stud-pattern)"></rect>
      <g class="htbi-diagonal-placement-preview" aria-hidden="true">${footprintMarkup(active, instanceKey)}</g>
      <rect class="htbi-diagonal-board-boundary" x="-1" y="-1" width="${board.widthStuds * 2}" height="${board.heightStuds * 2}"></rect>
      <g>
        ${viewModel.surfaceGroups.map((group) => {
      const destination = destinationMap.get(group.key);
      const hasSharedNativeAndDerived = group.hasNative && group.hasDerived;
      const variants = orderedSurfaceVariants(group.variants).map((point) => {
        const resultVariant = destination?.resultVariants.find((variant) => variant.surfacePointId === point.id);
        const isDestination = Boolean(resultVariant);
        const destinationClasses = [
          isDestination ? "htbi-diagonal-destination-available" : "",
          viewModel.previewResult?.end.surfacePointId === point.id ? "htbi-diagonal-destination-preview" : "",
          selected?.end.surfacePointId === point.id ? "htbi-diagonal-destination-selected" : "",
          hasSharedNativeAndDerived && point.originKind === "derived" ? "htbi-diagonal-surface-variant-direct" : ""
        ].filter(Boolean).join(" ");
        const pointClasses = [
          point.id === startId ? "htbi-diagonal-point-start" : "",
          isDestination ? "htbi-diagonal-point-destination" : ""
        ].filter(Boolean).join(" ");
        const hitTarget = hasSharedNativeAndDerived && point.originKind === "derived" ? "" : `<path class="htbi-diagonal-hit-target" data-surface-point-id="${point.id}" data-surface-variant-kind="${point.originKind}" d="${surfaceHitTargetPath(point)}"></path>`;
        return `<g class="${destinationClasses}" data-surface-point-id="${point.id}" data-surface-variant-kind="${point.originKind}"${resultVariant ? ` data-result-id="${resultVariant.resultId}"` : ""}>
              ${hitTarget}
              ${pointMarker(point, pointClasses, isDestination)}
            </g>`;
      }).join("");
      return `<g aria-hidden="true">
            ${variants}
            ${destination?.variantCount > 1 ? `<g data-result-id="${destination.preferredResultId}"><text class="htbi-diagonal-count" x="${group.x + 0.55}" y="${group.y - 0.55}">${destination.variantCount}</text></g>` : ""}
          </g>`;
    }).join("")}
      </g>
      ${active ? `<g class="htbi-diagonal-placed-footprint-grid" aria-hidden="true">
        ${viewModel.placedFootprintPoints.filter((point) => !point.anchorRole).map(placedFootprintPointMarkup).join("")}
        ${viewModel.placedFootprintPoints.filter((point) => point.anchorRole).map(placedFootprintPointMarkup).join("")}
      </g>` : ""}
      ${selected ? `
        <g class="htbi-diagonal-placement-annotation" aria-hidden="true">
          <line class="htbi-diagonal-guide" x1="${selected.start.surface.x}" y1="${selected.start.surface.y}" x2="${selected.end.surface.x}" y2="${selected.start.surface.y}"></line>
          <line class="htbi-diagonal-guide" x1="${selected.end.surface.x}" y1="${selected.start.surface.y}" x2="${selected.end.surface.x}" y2="${selected.end.surface.y}"></line>
          <path class="htbi-diagonal-angle-arc" d="${angleArc(selected, angleMetrics.radius)}"></path>
          <text class="htbi-diagonal-angle-label" style="font-size:${angleMetrics.fontSize}px" x="${selected.start.surface.x + angleMetrics.radius + 0.25}" y="${selected.start.surface.y - 0.25}">${formatNumber(selected.angleDegrees)}&deg;</text>
          <circle class="htbi-diagonal-anchor htbi-diagonal-anchor-start" cx="${selected.start.surface.x}" cy="${selected.start.surface.y}" r="0.52"></circle>
          <circle class="htbi-diagonal-anchor htbi-diagonal-anchor-end" cx="${selected.end.surface.x}" cy="${selected.end.surface.y}" r="0.52"></circle>
        </g>` : ""}
      <circle data-keyboard-cursor class="htbi-diagonal-keyboard-cursor" cx="${state.cursor.x}" cy="${state.cursor.y}" r="0.7"></circle>
      ${startPoint ? `
        <g class="htbi-diagonal-start-anchor-marker" data-start-anchor-id="${startPoint.id}" data-start-anchor-kind="${startPoint.originKind}" aria-hidden="true">
          <circle class="htbi-diagonal-start-anchor-halo" cx="${startPoint.x}" cy="${startPoint.y}" r="0.62"></circle>
          <circle class="htbi-diagonal-start-anchor-ring" cx="${startPoint.x}" cy="${startPoint.y}" r="0.54"></circle>
        </g>` : ""}
    </svg>`;
  }

  // src/ui/destination-interactions.js
  function destinationTarget(target) {
    return target?.closest?.("[data-result-id]") ?? null;
  }
  function bindDestinationInteractions({ board, resultList, onPreview, onClearPreview, onSelect }) {
    const previewFromEvent = (event) => {
      const target = destinationTarget(event.target);
      if (target) onPreview(target.dataset.resultId);
    };
    const selectFromBoard = (event) => {
      const target = destinationTarget(event.target);
      if (target) onSelect(target.dataset.resultId, { restoreTextualFocus: false });
    };
    const selectFromResultList = (event) => {
      const target = destinationTarget(event.target);
      if (target) onSelect(target.dataset.resultId, { restoreTextualFocus: true });
    };
    const selectFromKeyboard = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const target = destinationTarget(event.target);
      if (!target) return;
      event.preventDefault();
      onSelect(target.dataset.resultId, { restoreTextualFocus: true });
    };
    const clearAfterResultFocus = (event) => {
      if (!resultList.contains(event.relatedTarget)) onClearPreview();
    };
    board.addEventListener("pointerover", previewFromEvent);
    board.addEventListener("pointerleave", onClearPreview);
    board.addEventListener("click", selectFromBoard);
    resultList.addEventListener("focusin", previewFromEvent);
    resultList.addEventListener("focusout", clearAfterResultFocus);
    resultList.addEventListener("pointerover", previewFromEvent);
    resultList.addEventListener("pointerleave", onClearPreview);
    resultList.addEventListener("click", selectFromResultList);
    resultList.addEventListener("keydown", selectFromKeyboard);
    return () => {
      board.removeEventListener("pointerover", previewFromEvent);
      board.removeEventListener("pointerleave", onClearPreview);
      board.removeEventListener("click", selectFromBoard);
      resultList.removeEventListener("focusin", previewFromEvent);
      resultList.removeEventListener("focusout", clearAfterResultFocus);
      resultList.removeEventListener("pointerover", previewFromEvent);
      resultList.removeEventListener("pointerleave", onClearPreview);
      resultList.removeEventListener("click", selectFromResultList);
      resultList.removeEventListener("keydown", selectFromKeyboard);
    };
  }

  // src/ui/footprint-interactions.js
  function footprintPointTarget(target) {
    return target?.closest?.("[data-footprint-point-id]") ?? null;
  }
  function bindFootprintInteractions({ preview, select, onSelect }) {
    const selectVisualPoint = (event) => {
      const target = footprintPointTarget(event.target);
      if (target) onSelect(target.dataset.footprintPointId);
    };
    const selectDropdownPoint = () => onSelect(select.value);
    preview.addEventListener("click", selectVisualPoint);
    select.addEventListener("change", selectDropdownPoint);
    return () => {
      preview.removeEventListener("click", selectVisualPoint);
      select.removeEventListener("change", selectDropdownPoint);
    };
  }

  // src/ui/footprint-renderer.js
  var FOOTPRINT_POINT_GEOMETRY = Object.freeze({
    nativeRadius: 0.4,
    derivedSide: 0.28
  });
  function pointKind(point) {
    if (point.coordinateKind === "cell-center") return "cell-centre derived";
    if (point.originKind === "derived") return "edge-half derived";
    return "native";
  }
  function footprintBasePointMarkup(point, selected = false) {
    const selectedClass = selected ? " htbi-diagonal-footprint-point-selected" : "";
    const native = point.originKind === "native" ? `<circle class="htbi-diagonal-footprint-point htbi-diagonal-footprint-point-native${selectedClass}" cx="${point.x}" cy="${point.y}" r="${FOOTPRINT_POINT_GEOMETRY.nativeRadius}"></circle>` : "";
    const derivedClasses = [
      "htbi-diagonal-footprint-point",
      "htbi-diagonal-footprint-point-derived",
      selected ? "htbi-diagonal-footprint-point-selected" : "",
      point.coordinateKind === "cell-center" ? "htbi-diagonal-footprint-point-cell-center" : "",
      point.coordinateKind === "edge-half-x" || point.coordinateKind === "edge-half-y" ? "htbi-diagonal-footprint-point-edge-half" : ""
    ].filter(Boolean).join(" ");
    const derived = point.originKind === "derived" ? `<rect class="${derivedClasses}" x="${point.x - FOOTPRINT_POINT_GEOMETRY.derivedSide / 2}" y="${point.y - FOOTPRINT_POINT_GEOMETRY.derivedSide / 2}" width="${FOOTPRINT_POINT_GEOMETRY.derivedSide}" height="${FOOTPRINT_POINT_GEOMETRY.derivedSide}" transform="rotate(45 ${point.x} ${point.y})"></rect>` : "";
    return `${native}${derived}`;
  }
  function footprintPointMarkup(group, selectedPointId) {
    return group.variants.map((point) => `
  <g data-footprint-point-id="${point.id}" data-point-kind="${pointKind(point)}">
    ${footprintBasePointMarkup(point, point.id === selectedPointId)}
  </g>`).join("");
  }
  function footprintSvgMarkup({ footprint, pointGroups, selectedPointId }) {
    const width = footprint.widthStuds * 2;
    const height = footprint.lengthStuds * 2;
    return `
    <svg class="htbi-diagonal-footprint-preview-svg" viewBox="-2 -2 ${width + 4} ${height + 4}" role="img" aria-label="Selectable local connection points on the nominal footprint">
      <rect class="htbi-diagonal-footprint-preview-shape" x="-1" y="-1" width="${width}" height="${height}"></rect>
      ${pointGroups.map((group) => footprintPointMarkup(group, selectedPointId)).join("")}
    </svg>`;
  }

  // src/ui/state.js
  function createInitialState() {
    return {
      raw: {
        boardWidth: "32",
        boardHeight: "32",
        footprintWidth: "2",
        footprintLength: "4"
      },
      validation: null,
      board: null,
      footprint: null,
      surfaceStartId: null,
      partStartId: null,
      previewResultId: null,
      selectedResultId: null,
      cursor: { x: 0, y: 0 },
      announcement: ""
    };
  }
  function withConfiguration(state, raw, validation) {
    return {
      ...state,
      raw: { ...raw },
      validation,
      board: validation.ok ? validation.board : null,
      footprint: validation.ok ? validation.footprint : null,
      surfaceStartId: null,
      partStartId: null,
      previewResultId: null,
      selectedResultId: null,
      cursor: { x: 0, y: 0 },
      announcement: validation.ok ? "Board and footprint updated. Choose connection points." : "Correct the highlighted dimensions."
    };
  }
  function withSelection(state, changes) {
    return { ...state, ...changes };
  }
  function withAnchorSelection(state, changes) {
    return {
      ...state,
      ...changes,
      previewResultId: null,
      selectedResultId: null
    };
  }
  function moveCursor(state, dx, dy) {
    if (!state.board) return state;
    const maxX = (state.board.widthStuds - 1) * 2;
    const maxY = (state.board.heightStuds - 1) * 2;
    return {
      ...state,
      cursor: {
        x: Math.max(0, Math.min(maxX, state.cursor.x + dx)),
        y: Math.max(0, Math.min(maxY, state.cursor.y + dy))
      }
    };
  }

  // src/ui/surface-interactions.js
  function surfacePointTarget(target) {
    return target?.closest?.("[data-surface-point-id]") ?? null;
  }
  function surfacePointIdFromTarget(target) {
    return surfacePointTarget(target)?.dataset?.surfacePointId ?? null;
  }
  function getKeyboardSurfacePoint(group) {
    if (!group?.variants?.length) return null;
    return group.variants.find((point) => point.originKind === "native") ?? [...group.variants].sort((a, b) => a.id.localeCompare(b.id))[0];
  }
  function bindSurfaceStartInteractions({ board, onSelect }) {
    const selectSurfacePoint = (event) => {
      if (event.target?.closest?.("[data-result-id]")) return;
      const pointId = surfacePointIdFromTarget(event.target);
      if (pointId) onSelect(pointId);
    };
    board.addEventListener("click", selectSurfacePoint);
    return () => board.removeEventListener("click", selectSurfacePoint);
  }

  // src/ui/ui-text.js
  var UI_TEXT = Object.freeze({
    title: "Visual Diagonal & Angle Explorer",
    introduction: "Choose two supported connections and see the rectangular footprint placed directly on the board.",
    nominalDisclaimer: "Footprints are nominal planning rectangles. They do not assert that a manufactured part or legal technique exists.",
    productionEmpty: "No enabled production connection rules are available. The board remains visible, but supported placements require an approved rule catalogue."
  });

  // src/ui/viewport.js
  function createFittedViewBox(board, footprintPolygon = [], padding = 2) {
    const boardBounds2 = {
      minX: -1,
      minY: -1,
      maxX: board.widthStuds * 2 - 1,
      maxY: board.heightStuds * 2 - 1
    };
    const bounds = footprintPolygon.reduce((current, point) => ({
      minX: Math.min(current.minX, point.x),
      minY: Math.min(current.minY, point.y),
      maxX: Math.max(current.maxX, point.x),
      maxY: Math.max(current.maxY, point.y)
    }), boardBounds2);
    return Object.freeze({
      x: bounds.minX - padding,
      y: bounds.minY - padding,
      width: bounds.maxX - bounds.minX + padding * 2,
      height: bounds.maxY - bounds.minY + padding * 2
    });
  }
  function zoomViewBox(viewBox, factor) {
    const width = viewBox.width * factor;
    const height = viewBox.height * factor;
    return Object.freeze({
      x: viewBox.x + (viewBox.width - width) / 2,
      y: viewBox.y + (viewBox.height - height) / 2,
      width,
      height
    });
  }
  function panViewBox(viewBox, dxRatio, dyRatio) {
    return Object.freeze({
      ...viewBox,
      x: viewBox.x + viewBox.width * dxRatio,
      y: viewBox.y + viewBox.height * dyRatio
    });
  }

  // src/ui/explorer.js
  function createInstanceKey() {
    if (globalThis.crypto?.randomUUID) return `htbi-diagonal-${globalThis.crypto.randomUUID()}`;
    return `htbi-diagonal-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  function shellMarkup(instanceKey) {
    return `
    <section class="htbi-diagonal-shell" aria-labelledby="${instanceKey}-title">
      <header class="htbi-diagonal-header">
        <p class="htbi-diagonal-eyebrow">Visual planning tool</p>
        <h1 class="htbi-diagonal-title" id="${instanceKey}-title">${UI_TEXT.title}</h1>
        <p>${UI_TEXT.introduction}</p>
      </header>

      <section class="htbi-diagonal-usage" aria-labelledby="${instanceKey}-usage-title">
        <h2 id="${instanceKey}-usage-title">How to use the explorer</h2>
        <ol class="htbi-diagonal-steps">
          <li><strong>Set size</strong><span>Choose the board and footprint dimensions.</span></li>
          <li><strong>Footprint connection</strong><span>Choose a point on the rectangular footprint.</span></li>
          <li><strong>Board start</strong><span>Choose the board point that receives it.</span></li>
          <li><strong>Destination</strong><span>Choose a highlighted supported endpoint.</span></li>
        </ol>
        <div class="htbi-diagonal-legend" aria-label="Visual legend">
          <span><i class="htbi-diagonal-legend-native" aria-hidden="true"></i>Native stud/connection</span>
          <span><i class="htbi-diagonal-legend-derived" aria-hidden="true"></i>Derived midpoint</span>
          <span><i class="htbi-diagonal-legend-start" aria-hidden="true"></i>Selected start</span>
          <span><i class="htbi-diagonal-legend-destination" aria-hidden="true"></i>Available destination</span>
          <span><i class="htbi-diagonal-legend-destination-selected" aria-hidden="true"></i>Selected destination</span>
          <span><i class="htbi-diagonal-legend-overhang" aria-hidden="true"></i>Footprint outside board</span>
        </div>
      </section>

      <form class="htbi-diagonal-controls" data-configuration-form novalidate>
        <fieldset class="htbi-diagonal-fieldset">
          <legend>Board size</legend>
          <small class="htbi-diagonal-limit-note" id="${instanceKey}-board-limits">Each board dimension: 1\u201364 studs.</small>
          <label>Width <input data-field="boardWidth" inputmode="numeric" maxlength="2" value="32" aria-describedby="${instanceKey}-board-limits ${instanceKey}-boardWidth-error"></label>
          <span class="htbi-diagonal-error" id="${instanceKey}-boardWidth-error" data-error="boardWidth"></span>
          <label>Height <input data-field="boardHeight" inputmode="numeric" maxlength="2" value="32" aria-describedby="${instanceKey}-board-limits ${instanceKey}-boardHeight-error"></label>
          <span class="htbi-diagonal-error" id="${instanceKey}-boardHeight-error" data-error="boardHeight"></span>
        </fieldset>
        <fieldset class="htbi-diagonal-fieldset">
          <legend>Nominal footprint</legend>
          <small class="htbi-diagonal-limit-note" id="${instanceKey}-footprint-limits">Each side: 1\u201332 studs.</small>
          <label>Width <input data-field="footprintWidth" inputmode="numeric" maxlength="2" value="2" aria-describedby="${instanceKey}-footprint-limits ${instanceKey}-footprintWidth-error"></label>
          <span class="htbi-diagonal-error" id="${instanceKey}-footprintWidth-error" data-error="footprintWidth"></span>
          <label>Length <input data-field="footprintLength" inputmode="numeric" maxlength="2" value="4" aria-describedby="${instanceKey}-footprint-limits ${instanceKey}-footprintLength-error"></label>
          <span class="htbi-diagonal-error" id="${instanceKey}-footprintLength-error" data-error="footprintLength"></span>
          <button class="htbi-diagonal-secondary-button" type="button" data-swap-footprint>Swap width and length</button>
        </fieldset>
        <button class="htbi-diagonal-primary-button" type="submit">Update board and footprint</button>
      </form>

      <p class="htbi-diagonal-disclaimer">${UI_TEXT.nominalDisclaimer}</p>

      <p class="htbi-diagonal-step-guidance" data-step-guidance role="status"></p>

      <div class="htbi-diagonal-workspace">
        <section class="htbi-diagonal-board-panel" aria-labelledby="${instanceKey}-board-title">
          <div class="htbi-diagonal-panel-heading">
            <div><h2 id="${instanceKey}-board-title">Connection board</h2><p>Choose a start, then inspect the highlighted destinations.</p></div>
            <div class="htbi-diagonal-viewport-controls" aria-label="Board view controls">
              <button type="button" data-view="zoom-in" aria-label="Zoom in">+</button>
              <button type="button" data-view="zoom-out" aria-label="Zoom out">\u2212</button>
              <button type="button" data-view="fit">Fit</button>
              <button type="button" data-view="left" aria-label="Pan left">\u2190</button>
              <button type="button" data-view="up" aria-label="Pan up">\u2191</button>
              <button type="button" data-view="down" aria-label="Pan down">\u2193</button>
              <button type="button" data-view="right" aria-label="Pan right">\u2192</button>
            </div>
          </div>
          <div class="htbi-diagonal-board" data-board></div>
          <label class="htbi-diagonal-select-label" for="${instanceKey}-surface-start">Starting surface connection</label>
          <select id="${instanceKey}-surface-start" data-surface-start></select>
        </section>

        <aside class="htbi-diagonal-part-panel" aria-labelledby="${instanceKey}-part-title">
          <h2 id="${instanceKey}-part-title">Footprint connection</h2>
          <p>Select the local point that maps to the board start.</p>
          <div data-footprint-preview></div>
          <label class="htbi-diagonal-select-label" for="${instanceKey}-footprint-start">Local footprint connection</label>
          <select id="${instanceKey}-footprint-start" data-footprint-start></select>
          <section class="htbi-diagonal-destination-info" aria-labelledby="${instanceKey}-destination-info-title" aria-live="polite" aria-atomic="true">
            <h3 id="${instanceKey}-destination-info-title">Destination position</h3>
            <div data-destination-info></div>
          </section>
        </aside>
      </div>

      <section class="htbi-diagonal-results-panel" aria-labelledby="${instanceKey}-results-title">
        <h2 id="${instanceKey}-results-title">Supported destinations</h2>
        <div data-status></div>
        <div class="htbi-diagonal-result-list" data-result-list></div>
        <div class="htbi-diagonal-result-detail" data-result-detail></div>
      </section>
      <span class="htbi-diagonal-visually-hidden" data-announcement role="status" aria-live="polite" aria-atomic="true"></span>
    </section>`;
  }
  function pointOption(point) {
    return `${formatPoint(point)} \u2014 ${point.originKind} \u2014 ${point.explanation}`;
  }
  function createExplorer(root, { methods, allowDevelopment = false }) {
    if (!root || root.dataset.htbiDiagonalMounted === "true") return null;
    root.dataset.htbiDiagonalMounted = "true";
    root.classList.add("htbi-diagonal-root");
    const instanceKey = createInstanceKey();
    root.innerHTML = shellMarkup(instanceKey);
    const enabledMethods = getEnabledVerifiedMethods(methods, { allowDevelopment });
    let state = createInitialState();
    state = withConfiguration(state, state.raw, validateConfiguration(state.raw));
    let engine = null;
    let viewModel = null;
    let viewBox = createFittedViewBox(state.board);
    const elements = {
      form: root.querySelector("[data-configuration-form]"),
      board: root.querySelector("[data-board]"),
      footprintPreview: root.querySelector("[data-footprint-preview]"),
      surfaceStart: root.querySelector("[data-surface-start]"),
      footprintStart: root.querySelector("[data-footprint-start]"),
      destinationInfo: root.querySelector("[data-destination-info]"),
      guidance: root.querySelector("[data-step-guidance]"),
      status: root.querySelector("[data-status]"),
      resultList: root.querySelector("[data-result-list]"),
      resultDetail: root.querySelector("[data-result-detail]"),
      announcement: root.querySelector("[data-announcement]")
    };
    function calculate() {
      if (!state.board || !state.footprint) {
        engine = { surfacePoints: [], partPoints: [], results: [], diagnostics: {} };
      } else {
        engine = findPlacements({
          board: state.board,
          footprint: state.footprint,
          surfaceStartId: state.surfaceStartId,
          partStartId: state.partStartId,
          methods,
          allowDevelopment
        });
      }
      if (state.selectedResultId && !engine.results.some((result) => result.id === state.selectedResultId) || state.previewResultId && !engine.results.some((result) => result.id === state.previewResultId)) {
        state = withSelection(state, { selectedResultId: null, previewResultId: null });
      }
      viewModel = createExplorerViewModel({ state, engine, methods: enabledMethods });
    }
    function renderErrors() {
      for (const fieldName of ["boardWidth", "boardHeight", "footprintWidth", "footprintLength"]) {
        const field = root.querySelector(`[data-field="${fieldName}"]`);
        const error = root.querySelector(`[data-error="${fieldName}"]`);
        const validation = state.validation?.fields?.[fieldName];
        error.textContent = validation?.message ?? "";
        field.setAttribute("aria-invalid", validation && !validation.ok ? "true" : "false");
      }
    }
    function renderSelectors() {
      const surfaceOptions = engine.surfacePoints.map((point) => `<option value="${point.id}"${point.id === state.surfaceStartId ? " selected" : ""}>${pointOption(point)}</option>`).join("");
      elements.surfaceStart.innerHTML = `<option value="">Choose a surface point</option>${surfaceOptions}`;
      elements.surfaceStart.disabled = engine.surfacePoints.length === 0;
      const partOptions = engine.partPoints.map((point) => `<option value="${point.id}"${point.id === state.partStartId ? " selected" : ""}>${pointOption(point)}</option>`).join("");
      elements.footprintStart.innerHTML = `<option value="">Choose a local point</option>${partOptions}`;
      elements.footprintStart.disabled = engine.partPoints.length === 0;
    }
    function renderBoard() {
      if (!state.board) {
        elements.board.innerHTML = "<p>Enter valid board dimensions.</p>";
        elements.footprintPreview.innerHTML = "";
        return;
      }
      viewModel = createExplorerViewModel({ state, engine, methods: enabledMethods });
      elements.board.innerHTML = boardSvgMarkup({ instanceKey, board: state.board, viewBox, viewModel, state });
      elements.footprintPreview.innerHTML = footprintSvgMarkup({ footprint: state.footprint, pointGroups: viewModel.partGroups, selectedPointId: state.partStartId });
    }
    function renderResults() {
      if (!state.validation?.ok) {
        elements.status.innerHTML = '<p class="htbi-diagonal-empty" role="alert">Correct the dimension errors to continue.</p>';
      } else if (enabledMethods.length === 0) {
        elements.status.innerHTML = `<p class="htbi-diagonal-empty" role="status">${UI_TEXT.productionEmpty}</p>`;
      } else if (!state.surfaceStartId && !state.partStartId) {
        elements.status.innerHTML = '<p class="htbi-diagonal-empty">Choose a board start point and a footprint connection point. Either may be selected first.</p>';
      } else if (!state.surfaceStartId) {
        elements.status.innerHTML = '<p class="htbi-diagonal-empty">Choose a board start point.</p>';
      } else if (!state.partStartId) {
        elements.status.innerHTML = '<p class="htbi-diagonal-empty">Choose a footprint connection point.</p>';
      } else if (viewModel.results.length === 0) {
        elements.status.innerHTML = '<p class="htbi-diagonal-empty" role="status">No supported destinations match these anchors.</p>';
      } else {
        elements.status.innerHTML = `<p class="htbi-diagonal-result-count" role="status">${viewModel.results.length} supported diagonal placement${viewModel.results.length === 1 ? "" : "s"} found. Valid destinations are highlighted on the board; choose one to preview and select the diagonal placement.</p>`;
      }
      elements.resultList.innerHTML = viewModel.results.map(({ result, text }, index) => `
      <button type="button" class="htbi-diagonal-result-button${result.id === state.selectedResultId ? " htbi-diagonal-result-button-selected" : ""}" data-result-id="${result.id}" aria-label="Select placement at ${formatPoint(result.end.surface)}" aria-pressed="${result.id === state.selectedResultId}" aria-describedby="${instanceKey}-result-${index}-text">
        <strong>${formatPoint(result.end.surface)}</strong>
        <span>${formatNumber(result.angleDegrees)}\xB0 \xB7 ${formatNumber(result.distance.studs)} studs \xB7 ${result.requiredRuleIds.length} rule${result.requiredRuleIds.length === 1 ? "" : "s"}</span>
        <span class="htbi-diagonal-visually-hidden" id="${instanceKey}-result-${index}-text">${text}</span>
      </button>`).join("");
      const selected = engine.results.find((result) => result.id === state.selectedResultId);
      if (!selected) {
        elements.resultDetail.innerHTML = "";
        return;
      }
      const methodMap = new Map(enabledMethods.map((method) => [method.id, method]));
      elements.resultDetail.innerHTML = `
      <h3>Selected placement</h3>
      <dl>
        <div><dt>Destination</dt><dd>${formatPoint(selected.end.surface)}</dd></div>
        <div><dt>Angle</dt><dd>${formatNumber(selected.angleDegrees)}\xB0 clockwise from right</dd></div>
        <div><dt>Horizontal offset</dt><dd>${formatSigned(selected.offsets.horizontalStuds)} studs</dd></div>
        <div><dt>Vertical offset</dt><dd>${formatSigned(selected.offsets.verticalStuds)} studs (positive downward)</dd></div>
        <div><dt>Diagonal distance</dt><dd>${formatNumber(selected.distance.studs)} studs</dd></div>
        <div><dt>Connection rules</dt><dd>${selected.requiredMethodIds.map((id) => methodMap.get(id)?.displayName ?? id).join(", ")}</dd></div>
        <div><dt>Intermediary</dt><dd>${selected.requiredMethodIds.map((id) => methodMap.get(id)?.intermediary?.displayName).filter(Boolean).join(", ") || "None required by these general rules"}</dd></div>
        <div><dt>Physical validity</dt><dd>${selected.support.physicallyVerified ? selected.support.physicalAssessments.map((assessment) => `${assessment.claim} Scope: ${assessment.scope}`).join(" ") : "Not assessed"}</dd></div>
        <div><dt>Board coverage</dt><dd>${selected.containedWithinBoard ? "Complete nominal footprint is inside the board" : "Nominal footprint partially extends outside the board"}</dd></div>
        <div><dt>Legal/stress-free status</dt><dd>${selected.support.legalStatus === "approved" ? selected.support.legalAssessments.map((assessment) => `${assessment.claim} Scope: ${assessment.scope}`).join(" ") : "Not assessed"}</dd></div>
      </dl>`;
    }
    function renderDestinationInfo() {
      const summary = viewModel.destinationSummary;
      if (!summary) {
        elements.destinationInfo.innerHTML = "<p>Hover or focus a green destination, or select one, to see its coordinates and offset from the start.</p>";
        return;
      }
      elements.destinationInfo.innerHTML = `
      <p><strong>Destination:</strong> ${summary.destinationText.replace("Destination: ", "")}</p>
      <p><strong>Offset from start:</strong> ${summary.offsetText.replace("Offset from start: ", "")}</p>
      <p>${summary.movementText}</p>`;
    }
    function renderGuidance() {
      if (!state.validation?.ok) elements.guidance.textContent = "Correct the dimension errors to continue.";
      else if (enabledMethods.length === 0) elements.guidance.textContent = "No verified production connection methods are available.";
      else if (!state.surfaceStartId && !state.partStartId) elements.guidance.textContent = "Default sizes are ready. Next: choose a footprint connection, or choose the board start first.";
      else if (!state.surfaceStartId) elements.guidance.textContent = "Next: choose the board start point that receives the selected footprint connection.";
      else if (!state.partStartId) elements.guidance.textContent = "Next: click or tap a footprint symbol, or choose a local connection from the dropdown.";
      else if (viewModel.results.length === 0) elements.guidance.textContent = "No supported diagonal destinations exist for these two anchors. Try another start or footprint point.";
      else if (!state.selectedResultId) elements.guidance.textContent = "Next: choose a highlighted destination on the board or in the supported-destinations list.";
      else elements.guidance.textContent = `Placement selected. Review its geometry and ${viewModel.selectedResult.containedWithinBoard ? "fully contained footprint" : "striped board overhang"} below.`;
    }
    function renderAll() {
      renderErrors();
      renderSelectors();
      renderBoard();
      renderDestinationInfo();
      renderGuidance();
      renderResults();
      elements.announcement.textContent = state.announcement;
    }
    function selectSurfacePoint(pointId, focusBoard = false) {
      state = withAnchorSelection(state, {
        surfaceStartId: pointId || null,
        announcement: pointId ? "Starting surface connection selected." : "Starting surface connection cleared."
      });
      if (state.board) viewBox = createFittedViewBox(state.board);
      calculate();
      renderAll();
      if (focusBoard) queueMicrotask(() => elements.board.querySelector("[data-board-svg]")?.focus());
    }
    function selectPartPoint(pointId) {
      state = withAnchorSelection(state, {
        partStartId: pointId || null,
        announcement: pointId ? "Local footprint connection selected." : "Local footprint connection cleared."
      });
      if (state.board) viewBox = createFittedViewBox(state.board);
      calculate();
      renderAll();
    }
    elements.form.addEventListener("submit", (event) => {
      event.preventDefault();
      const raw = Object.fromEntries([...elements.form.querySelectorAll("[data-field]")].map((input) => [input.dataset.field, input.value]));
      const validation = validateConfiguration(raw);
      state = withConfiguration(state, raw, validation);
      if (validation.ok) viewBox = createFittedViewBox(validation.board);
      calculate();
      renderAll();
    });
    root.querySelector("[data-swap-footprint]").addEventListener("click", () => {
      const width = root.querySelector('[data-field="footprintWidth"]');
      const length = root.querySelector('[data-field="footprintLength"]');
      [width.value, length.value] = [length.value, width.value];
      elements.form.requestSubmit();
    });
    elements.surfaceStart.addEventListener("change", () => selectSurfacePoint(elements.surfaceStart.value));
    bindFootprintInteractions({
      preview: elements.footprintPreview,
      select: elements.footprintStart,
      onSelect: selectPartPoint
    });
    bindSurfaceStartInteractions({ board: elements.board, onSelect: selectSurfacePoint });
    function previewResult(resultId) {
      if (!viewModel.results.some(({ result }) => result.id === resultId)) return;
      state = withSelection(state, { previewResultId: resultId });
      renderBoard();
      renderDestinationInfo();
    }
    function clearPreview() {
      if (!state.previewResultId) return;
      state = withSelection(state, { previewResultId: null });
      renderBoard();
      renderDestinationInfo();
    }
    function restoreResultFocus(resultId) {
      const replacement = elements.resultList.querySelector(`[data-result-id="${resultId}"]`);
      const fallback = replacement ?? elements.resultList.querySelector("[data-result-id]") ?? elements.resultList;
      if (fallback === elements.resultList) fallback.setAttribute("tabindex", "-1");
      try {
        fallback.focus({ preventScroll: true });
      } catch {
        fallback.focus();
      }
    }
    function selectResult(resultId, { restoreTextualFocus = false } = {}) {
      const result = viewModel.results.find((candidate) => candidate.result.id === resultId)?.result;
      if (!result) return;
      state = withSelection(state, { selectedResultId: resultId, previewResultId: null, announcement: "Placement selected. Result details updated." });
      viewBox = createFittedViewBox(state.board, result.footprintPolygon);
      viewModel = createExplorerViewModel({ state, engine, methods: enabledMethods });
      renderBoard();
      renderDestinationInfo();
      renderGuidance();
      renderResults();
      elements.announcement.textContent = state.announcement;
      if (restoreTextualFocus) restoreResultFocus(resultId);
    }
    bindDestinationInteractions({
      board: elements.board,
      resultList: elements.resultList,
      onPreview: previewResult,
      onClearPreview: clearPreview,
      onSelect: selectResult
    });
    elements.board.addEventListener("keydown", (event) => {
      const steps = { ArrowLeft: [-2, 0], ArrowRight: [2, 0], ArrowUp: [0, -2], ArrowDown: [0, 2] };
      if (steps[event.key]) {
        event.preventDefault();
        state = moveCursor(state, ...steps[event.key]);
        const cursor = elements.board.querySelector("[data-keyboard-cursor]");
        cursor?.setAttribute("cx", state.cursor.x);
        cursor?.setAttribute("cy", state.cursor.y);
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        const key = `${state.cursor.x}:${state.cursor.y}`;
        const group = viewModel.surfaceGroups.find((candidate) => candidate.key === key);
        const point = getKeyboardSurfacePoint(group);
        if (point) selectSurfacePoint(point.id, true);
      }
    });
    root.querySelector(".htbi-diagonal-viewport-controls").addEventListener("click", (event) => {
      const action = event.target.dataset.view;
      if (!action || !state.board) return;
      if (action === "fit") viewBox = createFittedViewBox(state.board, viewModel.selectedResult?.footprintPolygon);
      else if (action === "zoom-in") viewBox = zoomViewBox(viewBox, 0.8);
      else if (action === "zoom-out") viewBox = zoomViewBox(viewBox, 1.25);
      else if (action === "left") viewBox = panViewBox(viewBox, -0.15, 0);
      else if (action === "right") viewBox = panViewBox(viewBox, 0.15, 0);
      else if (action === "up") viewBox = panViewBox(viewBox, 0, -0.15);
      else if (action === "down") viewBox = panViewBox(viewBox, 0, 0.15);
      renderBoard();
    });
    calculate();
    renderAll();
    return Object.freeze({ root, getState: () => state, getDiagnostics: () => engine.diagnostics });
  }
  function mountDiagonalAngleExplorers(scope = document, options) {
    return [...scope.querySelectorAll("[data-htbi-diagonal-angle-explorer]")].map((root) => createExplorer(root, options)).filter(Boolean);
  }

  // src/ui/main.js
  function initialize() {
    mountDiagonalAngleExplorers(document, {
      methods: PRODUCTION_CONNECTION_METHODS,
      allowDevelopment: false
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialize, { once: true });
  else initialize();
})();
